import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

// ─────────────────────────────────────────────
//  Thème clair / sombre.
//  Préférence de l'utilisateur : "light" | "dark" | "system" (suit l'appareil).
//  Mémorisée dans le navigateur (localStorage) ; "light" par défaut pour
//  garder l'apparence actuelle tant que personne n'a choisi.
//  Le thème actif s'applique via <html data-theme="light|dark"> : les couleurs
//  sont des variables CSS (voir styles/global.css), rien à changer composant par composant.
// ─────────────────────────────────────────────

const STORAGE_KEY = "deskora.theme";
const VALID = ["light", "dark", "system"];

function readPreference() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(v) ? v : "light";
  } catch {
    return "light"; // stockage bloqué (navigation privée…) : sans conséquence
  }
}

const systemQuery = () =>
  typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;

const resolveTheme = (pref) => (pref === "system" ? (systemQuery()?.matches ? "dark" : "light") : pref);

/** Applique le thème sur <html> et renvoie le thème effectif ("light" ou "dark"). */
export function applyTheme(pref) {
  const resolved = resolveTheme(pref);
  const el = document.documentElement;
  el.setAttribute("data-theme", resolved);
  el.setAttribute("data-bs-theme", resolved); // composants Bootstrap (formulaires, tableaux…)
  el.style.colorScheme = resolved;            // barres de défilement, champs date, etc.
  return resolved;
}

/** À appeler avant le premier rendu (main.jsx) pour éviter un flash de thème. */
export function initTheme() {
  return applyTheme(readPreference());
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [pref, setPrefState] = useState(readPreference);
  const [resolved, setResolved] = useState(() => resolveTheme(readPreference()));

  const setPref = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setPrefState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignoré */ }
  }, []);

  // Applique le thème à chaque changement de préférence
  useEffect(() => { setResolved(applyTheme(pref)); }, [pref]);

  // En mode « Automatique » : suit l'appareil en direct
  useEffect(() => {
    if (pref !== "system") return;
    const mq = systemQuery();
    if (!mq) return;
    const onChange = () => setResolved(applyTheme("system"));
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [pref]);

  // Synchronise les autres onglets ouverts
  useEffect(() => {
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setPrefState(readPreference()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({ pref, resolved, setPref }), [pref, resolved, setPref]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme doit être utilisé dans <ThemeProvider>");
  return ctx;
}
